import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { RootState } from '../../store/store';
import { toast } from 'react-hot-toast';
import { doc, getDoc, updateDoc, deleteDoc, setDoc, collection, query, where, getDocs, orderBy } from 'firebase/firestore';
import { db } from '../../firebase';
import { deleteUser, updatePassword, EmailAuthProvider, reauthenticateWithCredential } from 'firebase/auth';
import { clearUser } from '../../store/slices/authSlice';
import { ArrowLeft } from 'lucide-react';
import ConnectAccount from './ConnectAccount';

interface AccountSettingsProps {
  inTabView?: boolean;
}

interface Transaction {
  id: string;
  type: 'stripe_payment' | 'rental_earning' | 'rental_payment' | 'withdrawal';
  amount: number;
  timestamp: string;
  userId: string;
  description?: string;
}

const AccountSettings: React.FC<AccountSettingsProps> = ({ inTabView = false }) => {
  const { user, loading } = useSelector((state: RootState) => state.auth);
  const { currentUser, signOut } = useAuth();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [activeTab, setActiveTab] = useState('profile');
  const [displayName, setDisplayName] = useState('');
  const [providerName, setProviderName] = useState('');
  const [accountBalance, setAccountBalance] = useState(0);
  const [saving, setSaving] = useState(false);
  const [loadingProfile, setLoadingProfile] = useState(true);
  
  // Password change states
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  
  // Account deletion state
  const [deleteConfirmPassword, setDeleteConfirmPassword] = useState('');
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  
  // Fund account states
  const [fundAmount, setFundAmount] = useState(50);
  const [isFunding, setIsFunding] = useState(false);

  const [transactions, setTransactions] = useState<Transaction[]>([]);

  // Fetch user profile data
  useEffect(() => {
    const fetchUserProfile = async () => {
      if (!user?.uid) return;
      
      try {
        setLoadingProfile(true);
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        
        if (userDoc.exists()) {
          const userData = userDoc.data();
          setDisplayName(userData.displayName || '');
          setProviderName(userData.providerName || '');
          setAccountBalance(userData.accountBalance || 0);
        }
      } catch (error) {
        console.error('Error fetching user profile:', error);
        toast.error('Failed to load user profile');
      } finally {
        setLoadingProfile(false);
      }
    };
    
    fetchUserProfile();
  }, [user]);

  useEffect(() => {
    const fetchAccountBalance = async () => {
      if (!user?.uid) return;
      
      try {
        setLoadingProfile(true);
        
        // Fetch all transactions
        const transactionsQuery = query(
          collection(db, "transactions"),
          where("userId", "==", user.uid),
          orderBy("timestamp", "desc")
        );
        
        const transactionsSnapshot = await getDocs(transactionsQuery);
        const transactionsData = transactionsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        })) as Transaction[];
        
        // Calculate balance from transactions
        let balance = 0;
        transactionsData.forEach(transaction => {
          switch (transaction.type) {
            case 'stripe_payment':
            case 'rental_earning':
              balance += transaction.amount;
              break;
            case 'rental_payment':
            case 'withdrawal':
              balance -= transaction.amount;
              break;
            default:
              console.warn('Unknown transaction type:', transaction.type);
          }
        });
        
        setAccountBalance(balance);
        setTransactions(transactionsData);
        setLoadingProfile(false);
      } catch (error) {
        console.error("Error fetching account balance:", error);
        setLoadingProfile(false);
      }
    };

    fetchAccountBalance();
  }, [user]);

  if (loading || !user) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  const handleProfileUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!user?.uid) {
      toast.error('You must be logged in to update your profile');
      return;
    }
    
    try {
      setSaving(true);
      
      const userRef = doc(db, 'users', user.uid);
      const userDoc = await getDoc(userRef);
      
      if (userDoc.exists()) {
        // Update existing document
        await updateDoc(userRef, {
          displayName,
          providerName
        });
      } else {
        // Create user document if it doesn't exist
        await setDoc(userRef, {
          displayName,
          providerName,
          email: user.email,
          accountBalance: 0,
          uid: user.uid,
          createdAt: new Date()
        });
      }
      
      toast.success('Profile updated successfully');
    } catch (error) {
      console.error('Error updating profile:', error);
      toast.error('Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!currentUser) {
      toast.error('You must be logged in to change your password');
      return;
    }
    
    if (newPassword !== confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }
    
    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    try {
      setSaving(true);
      
      // Re-authenticate the user first
      const credential = EmailAuthProvider.credential(
        currentUser.email!,
        currentPassword
      );
      
      await reauthenticateWithCredential(currentUser, credential);
      
      // Update password
      await updatePassword(currentUser, newPassword);
      
      // Reset form
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      
      toast.success('Password updated successfully');
    } catch (error: any) {
      console.error('Error changing password:', error);
      
      if (error.code === 'auth/wrong-password') {
        toast.error('Current password is incorrect');
      } else {
        toast.error('Failed to update password');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleAccountDeletion = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!currentUser) {
      toast.error('You must be logged in to delete your account');
      return;
    }
    
    if (deleteConfirmText !== 'DELETE') {
      toast.error('Please type DELETE to confirm');
      return;
    }
    
    try {
      setSaving(true);
      
      // Re-authenticate the user first
      const credential = EmailAuthProvider.credential(
        currentUser.email!,
        deleteConfirmPassword
      );
      
      await reauthenticateWithCredential(currentUser, credential);
      
      // Delete Firestore user document
      await deleteDoc(doc(db, 'users', user.uid));
      
      // Delete Auth user
      await deleteUser(currentUser);
      
      // Clear Redux state
      dispatch(clearUser());
      
      toast.success('Account deleted successfully');
      navigate('/');
    } catch (error: any) {
      console.error('Error deleting account:', error);
      
      if (error.code === 'auth/wrong-password') {
        toast.error('Password is incorrect');
      } else {
        toast.error('Failed to delete account');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleFundAccount = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!user?.uid) {
      toast.error('You must be logged in to fund your account');
      return;
    }
    
    if (fundAmount < 5) {
      toast.error('Minimum funding amount is $5');
      return;
    }
    
    try {
      setIsFunding(true);
      
      // Get API URL from environment or use updated fallback with port 5002
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5002';
      
      // Make request to backend
      const response = await fetch(`${apiUrl}/api/venice/add-credit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userId: user.uid,
          amount: fundAmount
        }),
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || 'Failed to create payment session');
      }
      
      // Redirect to Stripe
      window.location.href = data.url;
    } catch (error: any) {
      console.error('Error funding account:', error);
      toast.error(error.message || 'Failed to fund account');
    } finally {
      setIsFunding(false);
    }
  };

  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const getTransactionIcon = (type: Transaction['type']): string => {
    switch (type) {
      case 'stripe_payment':
        return '💳';
      case 'rental_earning':
        return '💰';
      case 'rental_payment':
        return '🏠';
      case 'withdrawal':
        return '📤';
      default:
        return '❓';
    }
  };

  const getTransactionLabel = (type: Transaction['type']): string => {
    switch (type) {
      case 'stripe_payment':
        return 'Added Credit';
      case 'rental_earning':
        return 'Rental Earning';
      case 'rental_payment':
        return 'Rental Payment';
      case 'withdrawal':
        return 'Withdrawal';
      default:
        return 'Unknown Transaction';
    }
  };

  return (
    <>
      {!inTabView ? (
        <div className="container mx-auto px-4 py-4">
          <h1 className="text-2xl font-bold mb-6">Account Settings</h1>
        </div>
      ) : (
        <h1 className="text-2xl font-bold mb-6">Account Settings</h1>
      )}
      
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        {/* Tab navigation */}
        <div className="flex border-b overflow-x-auto">
          <button
            className={`px-6 py-3 text-sm font-medium ${activeTab === 'profile' ? 'text-blue-600 dark:text-blue-400 border-b-2 border-blue-600 dark:border-blue-400' : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'}`}
            onClick={() => setActiveTab('profile')}
          >
            Profile
          </button>
          <button
            className={`px-6 py-3 text-sm font-medium ${activeTab === 'security' ? 'text-blue-600 dark:text-blue-400 border-b-2 border-blue-600 dark:border-blue-400' : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'}`}
            onClick={() => setActiveTab('security')}
          >
            Security
          </button>
          <button
            className={`px-6 py-3 text-sm font-medium ${activeTab === 'fund' ? 'text-blue-600 dark:text-blue-400 border-b-2 border-blue-600 dark:border-blue-400' : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'}`}
            onClick={() => setActiveTab('fund')}
          >
            Fund Account
          </button>
          <button
            className={`px-6 py-3 text-sm font-medium ${activeTab === 'transactions' ? 'text-blue-600 dark:text-blue-400 border-b-2 border-blue-600 dark:border-blue-400' : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'}`}
            onClick={() => setActiveTab('transactions')}
          >
            Transactions
          </button>
          <button
            className={`px-6 py-3 text-sm font-medium ${activeTab === 'withdraw' ? 'text-blue-600 dark:text-blue-400 border-b-2 border-blue-600 dark:border-blue-400' : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'}`}
            onClick={() => setActiveTab('withdraw')}
          >
            Withdraw Funds
          </button>
          <button
            className={`px-6 py-3 text-sm font-medium ${activeTab === 'danger' ? 'text-red-600 border-b-2 border-red-600' : 'text-gray-500 hover:text-gray-700'}`}
            onClick={() => setActiveTab('danger')}
          >
            Delete Account
          </button>
        </div>
        
        <div className="p-6">
          {/* Profile tab */}
          {activeTab === 'profile' && (
            <div>
              <h2 className="text-xl font-semibold mb-4">Profile Information</h2>
              {loadingProfile ? (
                <div className="flex justify-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                </div>
              ) : (
                <form onSubmit={handleProfileUpdate}>
                  <div className="mb-4">
                    <label className="block text-gray-700 dark:text-gray-200 text-sm font-medium mb-2">
                      Email
                    </label>
                    <input
                      type="email"
                      className="bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md py-2 px-4 block w-full text-gray-700 dark:text-gray-200 cursor-not-allowed"
                      value={user.email || ''}
                      disabled
                    />
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Email cannot be changed</p>
                  </div>
                  <div className="mb-4">
                    <label className="block text-gray-700 dark:text-gray-200 text-sm font-medium mb-2">
                      Display Name
                    </label>
                    <input
                      type="text"
                      className="border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 rounded-md py-2 px-4 block w-full text-gray-700 dark:text-gray-200"
                      value={displayName}
                      onChange={(e) => setDisplayName(e.target.value)}
                      placeholder="Your display name"
                    />
                  </div>
                  <div className="mb-4">
                    <label className="block text-gray-700 dark:text-gray-200 text-sm font-medium mb-2">
                      Provider Name (shown to renters)
                    </label>
                    <input
                      type="text"
                      className="border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 rounded-md py-2 px-4 block w-full text-gray-700 dark:text-gray-200"
                      value={providerName}
                      onChange={(e) => setProviderName(e.target.value)}
                      placeholder="Your provider name"
                    />
                  </div>
                  <div className="mb-4">
                    <label className="block text-gray-700 dark:text-gray-200 text-sm font-medium mb-2">
                      Account Balance
                    </label>
                    <div className="bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md py-2 px-4 block w-full text-gray-700 dark:text-gray-200">
                      {formatCurrency(accountBalance)}
                    </div>
                  </div>
                  <div className="flex justify-end">
                    <button
                      type="submit"
                      className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-md transition-colors disabled:bg-blue-400"
                      disabled={saving}
                    >
                      {saving ? 'Saving...' : 'Save Changes'}
                    </button>
                  </div>
                </form>
              )}
            </div>
          )}
          
          {/* Security tab */}
          {activeTab === 'security' && (
            <div>
              <h2 className="text-xl font-semibold mb-4">Change Password</h2>
              <form onSubmit={handlePasswordChange}>
                <div className="mb-4">
                  <label className="block text-gray-700 dark:text-gray-200 text-sm font-medium mb-2">
                    Current Password
                  </label>
                  <input
                    type="password"
                    className="border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 rounded-md py-2 px-4 block w-full text-gray-700 dark:text-gray-200"
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    required
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-gray-700 dark:text-gray-200 text-sm font-medium mb-2">
                    New Password
                  </label>
                  <input
                    type="password"
                    className="border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 rounded-md py-2 px-4 block w-full text-gray-700 dark:text-gray-200"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                    minLength={6}
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-gray-700 dark:text-gray-200 text-sm font-medium mb-2">
                    Confirm New Password
                  </label>
                  <input
                    type="password"
                    className="border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 rounded-md py-2 px-4 block w-full text-gray-700 dark:text-gray-200"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    minLength={6}
                  />
                </div>
                <div className="flex justify-end">
                  <button
                    type="submit"
                    className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-md transition-colors disabled:bg-blue-400"
                    disabled={saving}
                  >
                    {saving ? 'Updating...' : 'Update Password'}
                  </button>
                </div>
              </form>
            </div>
          )}
          
          {/* Fund Account tab */}
          {activeTab === 'fund' && (
            <div>
              <h2 className="text-xl font-semibold mb-4">Fund Your Account</h2>
              <div className="mb-4 p-4 bg-blue-50 rounded-md">
                <p className="text-blue-800">
                  Your current balance: <span className="font-bold">{formatCurrency(accountBalance)}</span>
                </p>
              </div>
              <form onSubmit={handleFundAccount}>
                <div className="mb-4">
                  <label className="block text-gray-700 dark:text-gray-200 text-sm font-medium mb-2">
                    Amount to Add ($)
                  </label>
                  <input
                    type="number"
                    min="5"
                    step="5"
                    className="border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 rounded-md py-2 px-4 block w-full text-gray-700 dark:text-gray-200"
                    value={fundAmount}
                    onChange={(e) => setFundAmount(Number(e.target.value))}
                    required
                  />
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Minimum amount: $5</p>
                </div>
                
                <div className="mb-4">
                  <div className="flex space-x-2">
                    {[25, 50, 100, 200].map(amount => (
                      <button
                        key={amount}
                        type="button"
                        className={`px-4 py-2 border rounded-md ${
                          fundAmount === amount 
                            ? 'bg-blue-100 border-blue-500 text-blue-700' 
                            : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                        }`}
                        onClick={() => setFundAmount(amount)}
                      >
                        ${amount}
                      </button>
                    ))}
                  </div>
                </div>
                
                <div className="mt-6">
                  <button
                    type="submit"
                    className="w-full bg-green-600 hover:bg-green-700 text-white font-medium py-3 px-4 rounded-md transition-colors disabled:bg-green-400 flex items-center justify-center"
                    disabled={isFunding || fundAmount < 5}
                  >
                    {isFunding ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Processing...
                      </>
                    ) : (
                      `Add $${fundAmount.toFixed(2)} to My Account`
                    )}
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Transactions tab */}
          {activeTab === 'transactions' && (
            <div>
              <h2 className="text-xl font-semibold mb-4">Transaction History</h2>
              <div className="mb-4 p-4 bg-blue-50 rounded-md">
                <p className="text-blue-800">
                  Current balance: <span className="font-bold">{formatCurrency(accountBalance)}</span>
                </p>
              </div>
              
              {transactions.length === 0 ? (
                <p className="text-gray-500 text-center py-4">No transactions found</p>
              ) : (
                <div className="space-y-4">
                  {transactions.map((transaction) => (
                    <div key={transaction.id} className="flex items-center justify-between p-4 border rounded-lg">
                      <div className="flex items-center space-x-4">
                        <span className="text-2xl">{getTransactionIcon(transaction.type)}</span>
                        <div>
                          <p className="font-semibold">{getTransactionLabel(transaction.type)}</p>
                          <p className="text-sm text-gray-500">
                            {new Date(transaction.timestamp).toLocaleDateString()}
                          </p>
                          {transaction.description && (
                            <p className="text-sm text-gray-600">{transaction.description}</p>
                          )}
                        </div>
                      </div>
                      <p className={`font-semibold ${
                        ['stripe_payment', 'rental_earning'].includes(transaction.type)
                          ? 'text-green-600'
                          : 'text-red-600'
                      }`}>
                        {['stripe_payment', 'rental_earning'].includes(transaction.type) ? '+' : '-'}
                        {formatCurrency(transaction.amount)}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Withdraw Funds tab */}
          {activeTab === 'withdraw' && (
            <div>
              <h2 className="text-xl font-semibold mb-4">Withdraw Funds</h2>
              <ConnectAccount />
            </div>
          )}
          
          {/* Danger Zone tab */}
          {activeTab === 'danger' && (
            <div>
              <h2 className="text-xl font-semibold mb-4 text-red-600">Delete Account</h2>
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-md">
                <p className="text-red-800 font-medium">Warning: This action cannot be undone.</p>
                <p className="text-red-700 mt-2">
                  Deleting your account will permanently remove all your data, including:
                </p>
                <ul className="list-disc list-inside mt-2 text-red-700">
                  <li>Your profile information</li>
                  <li>Your VCU offers</li>
                  <li>Your rental history</li>
                  <li>Your account balance</li>
                </ul>
              </div>
              
              <form onSubmit={handleAccountDeletion}>
                <div className="mb-4">
                  <label className="block text-gray-700 text-sm font-medium mb-2">
                    Your Password
                  </label>
                  <input
                    type="password"
                    className="border border-gray-300 rounded-md py-2 px-4 block w-full"
                    value={deleteConfirmPassword}
                    onChange={(e) => setDeleteConfirmPassword(e.target.value)}
                    required
                  />
                </div>
                
                <div className="mb-4">
                  <label className="block text-gray-700 text-sm font-medium mb-2">
                    Type DELETE to confirm
                  </label>
                  <input
                    type="text"
                    className="border border-gray-300 rounded-md py-2 px-4 block w-full"
                    value={deleteConfirmText}
                    onChange={(e) => setDeleteConfirmText(e.target.value)}
                    required
                  />
                </div>
                
                <div className="mt-6">
                  <button
                    type="submit"
                    className="w-full bg-red-600 hover:bg-red-700 text-white font-medium py-3 px-4 rounded-md transition-colors disabled:bg-red-400"
                    disabled={saving || deleteConfirmText !== 'DELETE'}
                  >
                    {saving ? 'Processing...' : 'Permanently Delete Account'}
                  </button>
                </div>
              </form>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default AccountSettings; 